import React from "react";
import Swal from "sweetalert2";
import ModalView from "../../../common/modal";
import TextInput from "../../../common/text-input";
import Button from "../../../common/button/button";
import Text from "../../../common/typography/text";
import APIService from "../../../Service/APIService";
import "./NewProviderModal.css";

const initialFormState = {
  Prefix: "Dr.",
  FirstName: "",
  LastName: "",
  Email: "",
  Phone: "",
  ProviderNPI: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewProviderModal = ({
  isOpen,
  onClose,
  accountID,
  onProviderCreated,
}) => {
  const [formValues, setFormValues] = React.useState(initialFormState);
  const [errors, setErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setFormValues(initialFormState);
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const setFieldValue = (fieldName) => (value) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [fieldName]: "",
    }));
  };

  const setProviderNPI = (value) => {
    const digitsOnly = String(value || "").replace(/\D/g, "").slice(0, 10);
    setFieldValue("ProviderNPI")(digitsOnly);
  };

  const validate = () => {
    const nextErrors = {};
    const email = formValues.Email.trim();
    const phone = formValues.Phone.trim();

    if (!formValues.FirstName.trim()) {
      nextErrors.FirstName = "First name is required";
    }
    if (!formValues.LastName.trim()) {
      nextErrors.LastName = "Last name is required";
    }
    if (email && !EMAIL_PATTERN.test(email)) {
      nextErrors.Email = "Enter a valid email address";
    }
    if (email.length > 320) {
      nextErrors.Email = "Email must be 320 characters or fewer";
    }
    if (phone.length > 50) {
      nextErrors.Phone = "Phone must be 50 characters or fewer";
    }
    if (!/^\d{10}$/.test(formValues.ProviderNPI.trim())) {
      nextErrors.ProviderNPI = "Provider NPI must be exactly 10 digits";
    }
    if (!accountID) {
      nextErrors.form = "Select an account before adding a provider";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isSubmitting || !validate()) return;

    setIsSubmitting(true);
    try {
      const apiService = new APIService();
      const response = await apiService.addDataToBackend(
        {
          AccountID: accountID,
          Prefix: formValues.Prefix.trim(),
          FirstName: formValues.FirstName.trim(),
          LastName: formValues.LastName.trim(),
          Email: formValues.Email.trim() || null,
          Phone: formValues.Phone.trim() || null,
          ProviderNPI: formValues.ProviderNPI.trim(),
        },
        "addaccountprovider",
      );

      const createdProvider = response?.data?.item;
      if (!createdProvider) {
        throw new Error("Provider was created, but the response was incomplete.");
      }

      await onProviderCreated?.(createdProvider);
      onClose?.();
      Swal.fire({
        icon: "success",
        title: "Provider Added",
        text: "Provider has been added successfully.",
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add provider. Please verify the details and try again.";
      setErrors((prev) => ({ ...prev, form: message }));
      Swal.fire({
        icon: "error",
        title: "Failed to add provider",
        text: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalView
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isSubmitting}
      modalTitle="New Provider"
      minWidth="800px"
      maxWidth="800px"
      parentClassname="new-provider-modal"
      textInputComponent={
        <div className="new-provider-field-group-wrapper">
          <div className="new-provider-field-row">
            <TextInput
              titleText="Salutation"
              placeholder="Dr."
              value={formValues.Prefix}
              setValue={setFieldValue("Prefix")}
              parentClassName="new-provider-salutation-field"
            />
            <TextInput
              titleText="First Name"
              placeholder="Timo"
              value={formValues.FirstName}
              setValue={setFieldValue("FirstName")}
              parentClassName="new-provider-name-field"
              errorText={errors.FirstName}
            />
            <TextInput
              titleText="Last Name"
              placeholder="Test"
              value={formValues.LastName}
              setValue={setFieldValue("LastName")}
              parentClassName="new-provider-name-field"
              errorText={errors.LastName}
            />
          </div>
          <TextInput
            titleText="Provider NPI"
            placeholder="Enter NPI"
            value={formValues.ProviderNPI}
            onChange={(event) => setProviderNPI(event.target.value)}
            parentClassName="new-provider-npi-field"
            errorText={errors.ProviderNPI}
          />
          <div className="new-provider-field-row">
            <TextInput
              titleText="Email"
              placeholder="provider@example.com"
              value={formValues.Email}
              setValue={setFieldValue("Email")}
              parentClassName="new-provider-contact-field"
              errorText={errors.Email}
            />
            <TextInput
              titleText="Phone"
              placeholder="Enter phone"
              value={formValues.Phone}
              setValue={setFieldValue("Phone")}
              parentClassName="new-provider-contact-field"
              errorText={errors.Phone}
            />
          </div>
        </div>
      }
      footerButtonWrapper={
        <div className="new-provider-footer">
          <Button
            variant="primary"
            className="new-provider-submit-button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            Add Provider
          </Button>
          <Text variant="tinyText" color="lightText">
            This does not give them access to Tissue Vault.
          </Text>
          {errors.form && (
            <Text variant="tinyText" color="failureColor">
              {errors.form}
            </Text>
          )}
        </div>
      }
    />
  );
};

export default NewProviderModal;
